import { useState } from "react";
import { Briefcase, Check, ChevronDown, Loader2, TriangleAlert, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { DeviceMenu, SizeBadge } from "@/components/device-menu";
import { initialRichHtml, RichLineEditor } from "@/components/rich-line-editor";
import { deviceTitle, sizeBadge, type EditorDevice, type EditorLine } from "./model";

export interface LineEditorSave {
    description_html?: string;
    qty?: number;
    unit_price?: number;
    assetId?: string | null;
    whole_job?: boolean;
}

/** The device box in the line editor: same menu as everywhere, no native select. */
export function DeviceBox({
    devices,
    assetId,
    wholeJob,
    fallbackLabel,
    onChange,
}: {
    devices: readonly EditorDevice[];
    assetId: string | null;
    wholeJob: boolean;
    fallbackLabel?: string | null;
    onChange: (next: { assetId: string | null; wholeJob: boolean }) => void;
}) {
    const d = assetId ? devices.find((x) => x.id === assetId) : null;
    return (
        <DeviceMenu
            devices={devices}
            mode="tag"
            onPick={(p) =>
                onChange(
                    p.kind === "device"
                        ? { assetId: p.device.id, wholeJob: false }
                        : { assetId: null, wholeJob: p.kind === "whole" },
                )
            }
            trigger={
                <button
                    type="button"
                    className="inline-flex h-9 max-w-full items-center gap-2 rounded-md border bg-card px-2 text-left text-sm"
                    aria-label="Device for this line"
                >
                    {d ? (
                        <>
                            <SizeBadge>{sizeBadge(d.size_label) ?? "—"}</SizeBadge>
                            <span className="truncate font-semibold">{deviceTitle(d)}</span>
                            {d.serial_number && (
                                <span className="font-mono text-[11px] text-muted-foreground">
                                    SN {d.serial_number}
                                </span>
                            )}
                        </>
                    ) : assetId ? (
                        <span className="truncate">{fallbackLabel ?? "Device"}</span>
                    ) : wholeJob ? (
                        <>
                            <Briefcase className="h-4 w-4 text-primary" /> Whole job
                        </>
                    ) : (
                        <>
                            <TriangleAlert className="h-4 w-4 text-warning" /> No device
                        </>
                    )}
                    <ChevronDown className="ml-auto h-4 w-4 opacity-60" />
                </button>
            }
        />
    );
}

/**
 * Editing one line in the grouped list: the rich words box (bold, italic,
 * bullets, numbers), qty, price and device. Only what changed is sent, so
 * opening and saving untouched words never marks a line as hand-worded.
 */
export function LineEditorPanel({
    line,
    devices,
    pending,
    onSave,
    onCancel,
}: {
    line: EditorLine;
    devices: readonly EditorDevice[];
    pending: boolean;
    onSave: (body: LineEditorSave) => void;
    onCancel: () => void;
}) {
    const [start] = useState(() => initialRichHtml(line.description_html, line.description));
    const [html, setHtml] = useState(start);
    const [qty, setQty] = useState(String(Number(line.qty)));
    const [price, setPrice] = useState(String(Number(line.unit_price)));
    const [assetId, setAssetId] = useState<string | null>(line.asset_id ?? null);
    const [wholeJob, setWholeJob] = useState<boolean>(!!line.whole_job);
    const save = () => {
        const body: LineEditorSave = {};
        if (html !== start) body.description_html = html;
        if (Number(qty) !== Number(line.qty)) body.qty = Number(qty);
        if (Number(price) !== Number(line.unit_price)) body.unit_price = Number(price);
        if (assetId !== (line.asset_id ?? null)) body.assetId = assetId;
        if (!assetId && wholeJob !== !!line.whole_job) body.whole_job = wholeJob;
        if (Object.keys(body).length === 0) onCancel();
        else onSave(body);
    };
    return (
        <div className="flex flex-col gap-3" data-testid="line-editor">
            <RichLineEditor initialHtml={start} onChange={setHtml} onSubmit={save} onCancel={onCancel} autoFocus />
            <div className="flex flex-wrap items-end gap-3">
                <div className="flex flex-col gap-1">
                    <Label className="text-xs">Qty</Label>
                    <Input
                        type="number"
                        className="h-9 w-20 text-right"
                        value={qty}
                        onChange={(e) => setQty(e.target.value)}
                    />
                </div>
                <div className="flex flex-col gap-1">
                    <Label className="text-xs">Unit price</Label>
                    <Input
                        type="number"
                        step="0.01"
                        className="h-9 w-28 text-right"
                        value={price}
                        onChange={(e) => setPrice(e.target.value)}
                    />
                </div>
                {!line.option_id && (
                    <div className="flex min-w-0 flex-col gap-1">
                        <Label className="text-xs">Device</Label>
                        <DeviceBox
                            devices={devices}
                            assetId={assetId}
                            wholeJob={wholeJob}
                            fallbackLabel={[line.asset_size, line.asset_name].filter(Boolean).join(" ")}
                            onChange={(n) => {
                                setAssetId(n.assetId);
                                setWholeJob(n.wholeJob);
                            }}
                        />
                    </div>
                )}
                <div className="ml-auto flex gap-2">
                    <Button size="sm" variant="outline" onClick={onCancel} disabled={pending}>
                        <X className="mr-1 h-4 w-4" />
                        Cancel
                    </Button>
                    <Button size="sm" onClick={save} disabled={pending}>
                        {pending ? (
                            <Loader2 className="mr-1 h-4 w-4 animate-spin" />
                        ) : (
                            <Check className="mr-1 h-4 w-4" />
                        )}
                        Save
                    </Button>
                </div>
            </div>
        </div>
    );
}
